import Badge from 'antd/lib/badge';
import Collapse from 'antd/lib/collapse';
import Descriptions from 'antd/lib/descriptions';
import Modal from 'antd/lib/modal/Modal';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import Tag from 'antd/lib/tag';
import Tooltip from 'antd/lib/tooltip';
import Typography from 'antd/lib/typography';
import sortBy from 'lodash/sortBy';
import React, { useState } from 'react';
import { HardwareDeviceStatus, HardwareServiceResult, IHardwareServiceState, IHardwareState } from '../../domain/ven/interface';
import { MemoizedDatetimePopover } from '../datetimePopover/datetimePopover';
import { DISABLED_COLOR, ERROR_COLOR, INFO_COLOR, PRIMARY_COLOR, SECONDARY_COLOR } from '../../theme';

type StatusValue = { color: string; text: string };

const WARNING_COLOR = '#fac42a';

const STATUS_CONFIG: Record<HardwareDeviceStatus, StatusValue> = {
    [HardwareDeviceStatus.IDLE]: { color: DISABLED_COLOR, text: 'Idle' },
    [HardwareDeviceStatus.UPCOMING_EVENT]: { color: INFO_COLOR, text: 'Upcoming Event' },
    [HardwareDeviceStatus.PRE_EVENT]: { color: WARNING_COLOR, text: 'Pre-Event' },
    [HardwareDeviceStatus.EVENT]: { color: PRIMARY_COLOR, text: 'Event' },
};

const SERVICE_RESULT_CONFIG: Record<HardwareServiceResult, StatusValue> = {
    [HardwareServiceResult.OK]: { color: PRIMARY_COLOR, text: 'OK' },
    [HardwareServiceResult.SKIPPED]: { color: DISABLED_COLOR, text: 'Skipped' },
    [HardwareServiceResult.ERROR]: { color: ERROR_COLOR, text: 'Error' },
};

const SERVICE_RESULT_ORDER: Record<HardwareServiceResult, number> = {
    [HardwareServiceResult.ERROR]: 0,
    [HardwareServiceResult.OK]: 1,
    [HardwareServiceResult.SKIPPED]: 2,
};

const SERVICE_TOOLTIPS: Record<string, string> = {
    'gem-oadr-poller':
        'Every 60s. Polls the demand response server (VTN) for events, decides whether to opt in or opt out, and saves events to the local database.',
    'gem-relay-controller':
        'Every 30s. Checks the event database and turns the relay on or off based on active demand response events. Also activates a warning LED 72 hours before an event starts (pre-event flow).',
    'gem-remote':
        'Every 60s. Checks if remote SSH access has been requested. If so, opens an ngrok tunnel and reports the connection URL. Automatically closes the tunnel after timeout.',
    'gem-network-watchdog':
        'Every 60min. Checks internet connectivity by pinging an external host. If the connection is down, restarts network interfaces. Reboots the device as a last resort.',
    'gem-netgear-watchdog':
        'Every 10min. Monitors the Netgear LTE modem. If the cellular connection is down, attempts recovery: sets APN, enables roaming, or reconnects LTE.',
    'gem-status-reporter': 'Every 10m. Reports device status (version, relay state, active events) to the DRMS (this popup).',
    'gem-scheduled-reboot': 'Daily. Reboots the device once per day to keep the system in a clean state.',
};

const STALE_THRESHOLD_MS = 30 * 60 * 1000; // 30 min

function isStaleState(hardwareState: IHardwareState): boolean {
    if (!hardwareState.published_at) return false;
    return Date.now() - Date.parse(hardwareState.published_at) > STALE_THRESHOLD_MS;
}

function hasServiceError(hardwareState: IHardwareState): boolean {
    if (!hardwareState.services) return false;
    return Object.values(hardwareState.services).some(s => s.result === HardwareServiceResult.ERROR);
}

const ServiceLabel: React.FC<{ name: string; service: IHardwareServiceState }> = ({ name, service }) => {
    const { color, text } = SERVICE_RESULT_CONFIG[service.result];
    return (
        <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
                <Typography.Text>{name}</Typography.Text>
                {SERVICE_TOOLTIPS[name] && (
                    <>
                        {' '}
                        <Tooltip title={SERVICE_TOOLTIPS[name]}>
                            <QuestionCircleOutlined style={{ color: SECONDARY_COLOR, fontSize: 12 }} />
                        </Tooltip>
                    </>
                )}
            </span>
            <Tag color={color} style={{ marginRight: 0 }}>
                {text}
            </Tag>
        </span>
    );
};

const ServiceDetails: React.FC<{ service: IHardwareServiceState }> = ({ service }) => {
    return (
        <Descriptions bordered colon size="small" column={1}>
            <Descriptions.Item label="Timestamp">
                {service.timestamp ? <MemoizedDatetimePopover timestamp={Date.parse(service.timestamp)} inline /> : '-'}
            </Descriptions.Item>
            {service.tunnel_started_at && (
                <Descriptions.Item label="Tunnel Started At">
                    <MemoizedDatetimePopover timestamp={Date.parse(service.tunnel_started_at)} inline />
                </Descriptions.Item>
            )}
            {service.ping_ms != null && <Descriptions.Item label="Ping">{service.ping_ms} ms</Descriptions.Item>}
            {service.gateway && <Descriptions.Item label="Gateway">{service.gateway}</Descriptions.Item>}
            {service.ip && <Descriptions.Item label="IP">{service.ip}</Descriptions.Item>}
            {service.message && <Descriptions.Item label="Message">{service.message}</Descriptions.Item>}
        </Descriptions>
    );
};

interface IHardwareStateTagProps {
    hardwareState?: IHardwareState | null;
}

export const HardwareStateTag: React.FC<IHardwareStateTagProps> = ({ hardwareState }) => {
    const [modalOpen, setModalOpen] = useState(false);

    if (!hardwareState) {
        return <></>;
    }

    const stale = isStaleState(hardwareState);
    const { color, text } = stale ? { color: ERROR_COLOR, text: 'Stale' } : STATUS_CONFIG[hardwareState.device_status];
    const showErrorDot = hasServiceError(hardwareState);
    const services = hardwareState.services ? sortBy(Object.entries(hardwareState.services), ([, s]) => SERVICE_RESULT_ORDER[s.result]) : [];

    return (
        <>
            <Badge dot={showErrorDot} offset={[-8, 1]} color={ERROR_COLOR}>
                <Tag color={color} style={{ cursor: 'pointer' }} onClick={() => setModalOpen(true)}>
                    {text}
                </Tag>
            </Badge>

            <Modal open={modalOpen} width={600} title="Hardware State" destroyOnHidden onCancel={() => setModalOpen(false)} footer={[]}>
                <Descriptions bordered colon size="small" column={1}>
                    <Descriptions.Item label="Device Status">
                        <Tag color={color}>{text}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Version">{hardwareState.version}</Descriptions.Item>
                    <Descriptions.Item label="Ngrok Version">{hardwareState.ngrok_version || 'N/A'}</Descriptions.Item>
                    <Descriptions.Item label="Published At">
                        {hardwareState.published_at ? <MemoizedDatetimePopover timestamp={Date.parse(hardwareState.published_at)} inline /> : '-'}
                    </Descriptions.Item>
                    {hardwareState.event_id && <Descriptions.Item label="Event ID">{hardwareState.event_id}</Descriptions.Item>}
                    {hardwareState.event_start && (
                        <Descriptions.Item label="Event Start">
                            <MemoizedDatetimePopover timestamp={Date.parse(hardwareState.event_start)} inline />
                        </Descriptions.Item>
                    )}
                </Descriptions>

                {services.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                        <Typography.Title level={5} style={{ margin: '0 0 8px' }}>
                            Services{' '}
                            <Tooltip title="One-shot systemd services running on the device. Each service reports its last execution result.">
                                <QuestionCircleOutlined style={{ color: SECONDARY_COLOR, fontSize: 14 }} />
                            </Tooltip>
                        </Typography.Title>
                        <Collapse
                            size="small"
                            items={services.map(([name, service]) => ({
                                key: name,
                                label: <ServiceLabel name={name} service={service} />,
                                children: <ServiceDetails service={service} />,
                            }))}
                        />
                    </div>
                )}
            </Modal>
        </>
    );
};
